const FORM_PREFILL_PARAM_NAMES = [
  "name",
  "full_name",
  "phone",
  "phone_number",
  "mobile",
  "phone_country",
  "country",
  "care",
  "opt_in_care",
  "sms_care",
  "marketing",
  "mkt",
  "opt_in_marketing",
  "sms_marketing",
  "sms_scope",
  "source_app",
  "prefill_source",
  "source_ref",
  "external_id",
  "ref",
];
const RETURN_URL_PARAM_NAMES = ["return_url", "return_to"];
const PREFILL_PARAM_NAMES = [
  ...FORM_PREFILL_PARAM_NAMES,
  ...RETURN_URL_PARAM_NAMES,
];

const TRUE_VALUES = new Set(["1", "true", "yes", "y", "on"]);
const FALSE_VALUES = new Set(["0", "false", "no", "n", "off"]);
const SOURCE_SAFE_CHARS = /[^A-Za-z0-9._:/-]+/g;
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g;
const RETURN_URL_HOST_SUFFIXES = [".zoni.edu", ".zonilive.com"];
const RETURN_URL_MAX_LENGTH = 2048;

function firstParam(params, names) {
  for (const name of names) {
    const value = params.get(name);
    if (value !== null) {
      return value;
    }
  }
  return "";
}

function hasAnyParam(params, names) {
  return names.some((name) => params.has(name));
}

function truncate(value, maxLength) {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function sanitizeSourceMetadata(value, maxLength) {
  const clean = String(value || "")
    .replace(CONTROL_CHARS, "")
    .trim()
    .replace(SOURCE_SAFE_CHARS, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return truncate(clean, maxLength);
}

function combineBooleanValues(...values) {
  if (values.some((value) => value === true)) {
    return true;
  }
  if (values.some((value) => value === false)) {
    return false;
  }
  return null;
}

function parseBooleanAliases(params, names) {
  const values = names
    .filter((name) => params.has(name))
    .map((name) => parsePrefillBoolean(params.get(name)));

  return combineBooleanValues(...values);
}

export function isAllowedSmsOptInReturnUrlHost(hostname) {
  const host = String(hostname || "").toLowerCase().replace(/\.$/, "");
  return RETURN_URL_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

export function sanitizePrefillName(value) {
  const clean = String(value || "")
    .replace(CONTROL_CHARS, "")
    .trim()
    .replace(/\s+/g, " ");

  return truncate(clean, 120);
}

export function sanitizePrefillPhone(value) {
  const clean = String(value || "")
    .replace(CONTROL_CHARS, "")
    .trim();

  return truncate(clean, 32);
}

export function parsePrefillBoolean(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (TRUE_VALUES.has(normalized)) {
    return true;
  }
  if (FALSE_VALUES.has(normalized)) {
    return false;
  }
  return null;
}

export function parseSmsScope(value) {
  let care = null;
  let marketing = null;

  for (const rawScope of String(value || "").split(",")) {
    const scope = rawScope.trim().toLowerCase();
    if (scope === "both") {
      care = true;
      marketing = true;
    } else if (scope === "care") {
      care = true;
    } else if (scope === "marketing" || scope === "mkt") {
      marketing = true;
    }
  }

  return { care, marketing };
}

export function sanitizeSmsOptInReturnUrl(value) {
  const clean = String(value || "")
    .replace(CONTROL_CHARS, "")
    .trim();

  if (!clean || clean.length > RETURN_URL_MAX_LENGTH) {
    return "";
  }

  let url;
  try {
    url = new URL(clean);
  } catch {
    return "";
  }

  if (url.protocol !== "https:") {
    return "";
  }
  if (url.username || url.password) {
    return "";
  }
  if (!isAllowedSmsOptInReturnUrlHost(url.hostname)) {
    return "";
  }

  return url.toString();
}

export function readSmsOptInPrefillFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const rawName = firstParam(params, ["name", "full_name"]);
  const rawPhone = firstParam(params, ["phone", "phone_number", "mobile"]);
  const rawSourceApp = firstParam(params, ["source_app", "prefill_source"]);
  const rawSourceRef = firstParam(params, ["source_ref", "external_id", "ref"]);
  const rawReturnUrl = firstParam(params, RETURN_URL_PARAM_NAMES);
  const scope = parseSmsScope(firstParam(params, ["sms_scope"]));

  const name = sanitizePrefillName(rawName);
  const phone = sanitizePrefillPhone(rawPhone);
  const sourceApp = sanitizeSourceMetadata(rawSourceApp, 80);
  const sourceRef = sanitizeSourceMetadata(rawSourceRef, 120);
  const returnUrl = sanitizeSmsOptInReturnUrl(rawReturnUrl);
  const care = combineBooleanValues(
    parseBooleanAliases(params, ["care", "opt_in_care", "sms_care"]),
    scope.care
  );
  const marketing = combineBooleanValues(
    parseBooleanAliases(params, [
      "marketing",
      "mkt",
      "opt_in_marketing",
      "sms_marketing",
    ]),
    scope.marketing
  );

  const hadPrefill = hasAnyParam(params, FORM_PREFILL_PARAM_NAMES);
  const hadReturnUrl = hasAnyParam(params, RETURN_URL_PARAM_NAMES);

  return {
    name,
    phone,
    phoneCountry: "US",
    care,
    marketing,
    sourceApp,
    sourceRef,
    returnUrl,
    hadPrefill,
    hadReturnUrl,
  };
}

export function scrubPrefillParamsFromUrl(prefill) {
  if (
    (!prefill?.hadPrefill && !prefill?.hadReturnUrl) ||
    !window.history?.replaceState
  ) {
    return;
  }

  const url = new URL(window.location.href);
  for (const name of PREFILL_PARAM_NAMES) {
    url.searchParams.delete(name);
  }

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, document.title, nextUrl);
}
